import type { ReactNode } from "react"
import { memo, useEffect, useState } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"

import {
	Loader,
	ScrollArea,
	ScrollViewport,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@productlane/ui"

import { ActivationFunnel } from "@/components/iluvatar/activation-funnel"
import { AdminCustomersTable } from "@/components/iluvatar/admin-customers-table"
import { GtmCampaigns } from "@/components/iluvatar/gtm-campaigns"
import { GtmSignupCheckins } from "@/components/iluvatar/gtm-signup-checkins"
import { MetaInfo } from "@/components/meta-info"
import { useSession } from "@/lib/session"
import { ActiveUsersChart } from "../../../components/iluvatar/active-users-chart"
import { AiAgentActiveWorkspacesChart } from "../../../components/iluvatar/ai-agent-active-workspaces-chart"
import { AiAgentKpiTiles } from "../../../components/iluvatar/ai-agent-kpi-tiles"
import { AIAutoRepliesChart } from "../../../components/iluvatar/ai-auto-replies-chart"
import { AiAutomationRates } from "../../../components/iluvatar/ai-automation-rates"
import { AIChangelogsChart } from "../../../components/iluvatar/ai-changelogs-chart"
import { AIChatMessagesChart } from "../../../components/iluvatar/ai-chat-messages-chart"
import { AICopilotMessagesChart } from "../../../components/iluvatar/ai-copilot-messages-chart"
import { AiCreditUsageChart } from "../../../components/iluvatar/ai-credit-usage-chart"
import { AIDocsProposalsChart } from "../../../components/iluvatar/ai-docs-proposals-chart"
import { AIDraftsChart } from "../../../components/iluvatar/ai-drafts-chart"
import { AiResolutionsChart } from "../../../components/iluvatar/ai-resolutions-chart"
import { AIResponsesLivestream } from "../../../components/iluvatar/ai-responses-livestream"
import { AISpendByFeatureChart } from "../../../components/iluvatar/ai-spend-by-feature-chart"
import { AISpendByWorkspaceTable } from "../../../components/iluvatar/ai-spend-by-workspace-table"
import { AITranslationsChart } from "../../../components/iluvatar/ai-translations-chart"
import { AIUndeployedWorklist } from "../../../components/iluvatar/ai-undeployed-worklist"
import { ApiErrorBreakdownChart } from "../../../components/iluvatar/api-error-breakdown-chart"
import { ApiRequestsChart } from "../../../components/iluvatar/api-requests-chart"
import { ApiSlowestEndpointsChart } from "../../../components/iluvatar/api-slowest-endpoints-chart"
import { ApiTopEndpointsChart } from "../../../components/iluvatar/api-top-endpoints-chart"
import { ApiTopWorkspacesChart } from "../../../components/iluvatar/api-top-workspaces-chart"
import { ConversationsChart } from "../../../components/iluvatar/conversations-chart"
import { ConversionRateChart } from "../../../components/iluvatar/conversion-rate-chart"
import { DocsChart } from "../../../components/iluvatar/docs-chart"
import { EmailChart } from "../../../components/iluvatar/email-chart"
import { EmailConnectedChart } from "../../../components/iluvatar/email-connected-chart"
import { FeatureAdoptionTable } from "../../../components/iluvatar/feature-adoption-table"
import { GrowthActivationChart } from "../../../components/iluvatar/growth-activation-chart"
import { LiveChatChart } from "../../../components/iluvatar/live-chat-chart"
import { MrrChart } from "../../../components/iluvatar/mrr-chart"
import { MrrEmailConnectedChart } from "../../../components/iluvatar/mrr-email-connected-chart"
import { PlanBreakdownChart } from "../../../components/iluvatar/plan-breakdown-chart"
import { SettingsAdoptionTable } from "../../../components/iluvatar/settings-adoption-table"
import { SlackChart } from "../../../components/iluvatar/slack-chart"
import { StickinessChart } from "../../../components/iluvatar/stickiness-chart"
import { SupportVolumeChart } from "../../../components/iluvatar/support-volume-chart"
import { WorkspaceCreationChart } from "../../../components/iluvatar/workspace-creation-chart"
import { PageHeader } from "../../app/page-header"

const StripeChartsSection = memo(() => {
	return (
		<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
			<MrrChart />
			<MrrEmailConnectedChart />
			<div className="col-span-1 md:col-span-2">
				<ConversionRateChart />
			</div>
			<div className="col-span-1 md:col-span-2">
				<PlanBreakdownChart />
			</div>
		</div>
	)
})

StripeChartsSection.displayName = "StripeChartsSection"

const PosthogChartsSection = memo(() => {
	return (
		<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
			<div className="col-span-1 md:col-span-2">
				<ActiveUsersChart />
			</div>
			<div className="col-span-1 md:col-span-2">
				<StickinessChart />
			</div>
		</div>
	)
})

PosthogChartsSection.displayName = "PosthogChartsSection"

const GrowthChartsSection = memo(() => {
	return (
		<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
			<div className="col-span-1 md:col-span-2">
				<AiAutomationRates />
			</div>
			<div className="col-span-1 md:col-span-2">
				<GrowthActivationChart />
			</div>
			<div className="col-span-1 md:col-span-2">
				<ConversionRateChart />
			</div>
			<WorkspaceCreationChart />
			<AiResolutionsChart />
			<EmailConnectedChart />
			<MrrChart />
		</div>
	)
})

GrowthChartsSection.displayName = "GrowthChartsSection"

const AiChartsSection = memo(() => {
	return (
		<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
			<div className="col-span-1 md:col-span-2">
				<AiAgentKpiTiles />
			</div>
			<AiResolutionsChart />
			<AiAgentActiveWorkspacesChart />
			<AiCreditUsageChart />
			<AIResponsesLivestream />
			<div className="col-span-1 md:col-span-2">
				<AIUndeployedWorklist />
			</div>
			<div className="col-span-1 md:col-span-2">
				<AISpendByFeatureChart />
			</div>
			<div className="col-span-1 md:col-span-2">
				<AISpendByWorkspaceTable />
			</div>
			<AIChatMessagesChart />
			<AICopilotMessagesChart />
			<AIDraftsChart />
			<AIDocsProposalsChart />
			<AIAutoRepliesChart />
			<AIChangelogsChart />
			<AITranslationsChart />
		</div>
	)
})

AiChartsSection.displayName = "AiChartsSection"

const ProductChartsSection = memo(() => {
	return (
		<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
			<div className="col-span-1 md:col-span-2">
				<ConversationsChart />
			</div>
			<WorkspaceCreationChart />
			<EmailConnectedChart />
			<DocsChart />
			<LiveChatChart />
			<SlackChart />
			<EmailChart />
			<SupportVolumeChart />
		</div>
	)
})

ProductChartsSection.displayName = "ProductChartsSection"

const ApiChartsSection = memo(() => {
	return (
		<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
			<div className="col-span-1 md:col-span-2">
				<ApiRequestsChart />
			</div>
			<ApiTopWorkspacesChart />
			<ApiTopEndpointsChart />
			<ApiErrorBreakdownChart />
			<ApiSlowestEndpointsChart />
		</div>
	)
})

ApiChartsSection.displayName = "ApiChartsSection"

const ADMIN_TABS = [
	{ value: "customers", label: "Customers" },
	{ value: "growth", label: "Growth" },
	{ value: "ai", label: "AI" },
	{ value: "stripe", label: "Stripe" },
	{ value: "posthog", label: "Posthog" },
	{ value: "product", label: "Product" },
	{ value: "api", label: "Api" },
	{ value: "features", label: "Features" },
	{ value: "funnel", label: "Funnel" },
	{ value: "checkins", label: "Signup check-ins" },
	{ value: "campaigns", label: "Campaigns" },
] as const

// Old ?tab= links pointed at the former section tabs.
const LEGACY_TAB_REDIRECTS: Record<string, string> = {
	reporting: "ai",
	gtm: "funnel",
}

function AdminTabScroll({ children }: { children: ReactNode }) {
	return (
		<div className="h-[calc(100dvh-6.75rem)] bg-white dark:bg-gray-900">
			<ScrollArea>
				<ScrollViewport>
					<div className="mx-auto max-w-[2000px] px-6 pb-12">{children}</div>
				</ScrollViewport>
			</ScrollArea>
		</div>
	)
}

function ChartsTab({
	showCharts,
	children,
}: {
	showCharts: boolean
	children: ReactNode
}) {
	return (
		<AdminTabScroll>
			{showCharts ? (
				children
			) : (
				<div className="flex h-64 items-center justify-center">
					<Loader />
				</div>
			)}
		</AdminTabScroll>
	)
}

export function IluvatarScreen() {
	const [showCharts, setShowCharts] = useState(false)
	const tabParam = useSearch({ strict: false, select: (s) => s.tab })
	const [tab, setTab] = useState(() => {
		const requested = tabParam ?? "ai"
		return LEGACY_TAB_REDIRECTS[requested] ?? requested
	})
	const [featureFilter, setFeatureFilter] = useState<string[]>([])
	const navigate = useNavigate()

	const { session, isLoaded } = useSession()
	useEffect(() => {
		if (isLoaded && session?.userRole !== "ADMIN") {
			void navigate({ to: "/threads" })
		}
	}, [isLoaded, session?.userRole, navigate])

	useEffect(() => {
		const timer = setTimeout(() => {
			setShowCharts(true)
		}, 100)
		return () => clearTimeout(timer)
	}, [])

	return (
		<>
			<MetaInfo title="Admin Area" />
			<Tabs value={tab} onValueChange={setTab}>
				<PageHeader title="Admin" clearsNavToggle />

				<div className="no-scrollbar flex h-[3.75rem] items-center overflow-x-auto bg-white px-6 dark:bg-gray-900">
					<TabsList className="w-fit justify-start">
						{ADMIN_TABS.map((adminTab) => (
							<TabsTrigger key={adminTab.value} value={adminTab.value}>
								{adminTab.label}
							</TabsTrigger>
						))}
					</TabsList>
				</div>

				<TabsContent value="customers">
					<AdminCustomersTable
						selectedFeatures={featureFilter}
						onSelectedFeaturesChange={setFeatureFilter}
					/>
				</TabsContent>

				<TabsContent value="growth">
					<ChartsTab showCharts={showCharts}>
						<GrowthChartsSection />
					</ChartsTab>
				</TabsContent>
				<TabsContent value="ai">
					<ChartsTab showCharts={showCharts}>
						<AiChartsSection />
					</ChartsTab>
				</TabsContent>
				<TabsContent value="stripe">
					<ChartsTab showCharts={showCharts}>
						<StripeChartsSection />
					</ChartsTab>
				</TabsContent>
				<TabsContent value="posthog">
					<ChartsTab showCharts={showCharts}>
						<PosthogChartsSection />
					</ChartsTab>
				</TabsContent>
				<TabsContent value="product">
					<ChartsTab showCharts={showCharts}>
						<ProductChartsSection />
					</ChartsTab>
				</TabsContent>
				<TabsContent value="api">
					<ChartsTab showCharts={showCharts}>
						<ApiChartsSection />
					</ChartsTab>
				</TabsContent>

				<TabsContent value="features">
					<AdminTabScroll>
						<FeatureAdoptionTable
							onFeatureClick={(featureName) => {
								setFeatureFilter([featureName])
								setTab("customers")
							}}
						/>
						<div className="mt-6">
							<SettingsAdoptionTable />
						</div>
					</AdminTabScroll>
				</TabsContent>

				<TabsContent value="funnel">
					<AdminTabScroll>
						<ActivationFunnel />
					</AdminTabScroll>
				</TabsContent>
				<TabsContent value="checkins">
					<AdminTabScroll>
						<GtmSignupCheckins />
					</AdminTabScroll>
				</TabsContent>
				<TabsContent value="campaigns">
					<AdminTabScroll>
						<GtmCampaigns />
					</AdminTabScroll>
				</TabsContent>
			</Tabs>
		</>
	)
}
